import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'

const routes = [
  // Public voter routes — no auth required
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/PublicLanding.vue'),
    meta: { public: true }
  },
  {
    path: '/vote',
    name: 'VotingPanel',
    component: () => import('@/views/VotingPanel.vue'),
    meta: { public: true }
  },
  {
    path: '/vote/success',
    name: 'VoteSuccess',
    component: () => import('@/views/VoteSuccess.vue'),
    meta: { public: true }
  },
  {
    path: '/live',
    name: 'LiveDashboard',
    component: () => import('@/views/LiveDashboard.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/committees',
    name: 'CommitteeRanking',
    component: () => import('@/views/CommitteeRanking.vue'),
    meta: { public: true }
  },
  {
    path: '/login',
    redirect: to => {
      const key = to.query.key || to.query.voter_key
      if (key) {
        return { path: '/vote', query: { voter_key: key } }
      }
      return { path: '/' }
    },
    meta: { public: true }
  },
  {
    path: '/login/direct/:key',
    redirect: to => {
      return { path: '/vote', query: { voter_key: to.params.key } }
    },
    meta: { public: true }
  },

  // Admin routes — require authentication
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@/views/AdminLogin.vue'),
    meta: { public: true, adminGuestOnly: true }
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () => import('@/views/AdminDashboard.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/admin/sessions',
    name: 'AdminSessions',
    component: () => import('@/views/AdminSessions.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/admin/sessions/:sessionId',
    name: 'AdminSessionControl',
    component: () => import('@/views/AdminSessionControl.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/admin/candidates',
    name: 'AdminCandidates',
    component: () => import('@/views/AdminCandidates.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/admin/results',
    name: 'AdminResults',
    component: () => import('@/views/AdminResults.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/admin/voters',
    name: 'AdminVoters',
    component: () => import('@/views/AdminVoters.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/admin/audit',
    name: 'AdminAuditLog',
    component: () => import('@/views/AdminAuditLog.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/admin/docs',
    name: 'AdminDocs',
    component: () => import('@/views/AdminDocs.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/admin/committees',
    name: 'AdminCommittees',
    component: () => import('@/views/AdminCommittees.vue'),
    meta: { requiresAdmin: true }
  },

  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { public: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    return { top: 0 }
  }
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  if (!authStore.initialized) {
    await authStore.initialize()
  }

  // Admin-only routes
  if (to.meta.requiresAdmin) {
    if (!authStore.isAuthenticated || !authStore.isAdmin) {
      return next({ name: 'AdminLogin', query: { redirect: to.fullPath } })
    }
  }

  // Redirect logged-in admins away from admin login
  if (to.meta.adminGuestOnly && authStore.isAuthenticated && authStore.isAdmin) {
    return next({ name: 'AdminDashboard' })
  }

  next()
})

export default router
